import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { Login, ForgotPassword, ResetPassword, ForcePasswordChange } from './features/auth';
import { Dashboard } from './features/dashboard';
import { Sites, SiteDetails } from './features/sites';
import { Assets } from './features/assets';
import { Tests } from './features/tests';
import { Repairs } from './features/repairs';
import { LandingPage } from './features/landing';
import Reports from './pages/Reports';
import {
  Settings,
  TeamSettings,
  ProfileSettings,
  AppearanceSettings,
  SecuritySettings,
  SettingsLayout,
} from './features/settings';
import { SuperAdminOrganizations, PlatformAdmins } from './features/super-admin';
import DevNotes from './pages/DevNotes';
import KitGalleryPage from './dev/KitGalleryPage';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuthStore } from './store/authStore';
import { ThemeProvider } from './context/ThemeContext';
import api from './lib/api';
import queryClient from './lib/queryClient';
import './App.css';

function SettingsIndexRedirect() {
  const user = useAuthStore((s) => s.user);
  const admin = (user?.role || '').toLowerCase() === 'admin';
  return <Navigate to={admin ? '/settings/organization' : '/settings/profile'} replace />;
}

function App() {
  const { authStatus, _hasHydrated, setUser, setAuthStatus } = useAuthStore();

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      const hasCsrfCookie = typeof document !== 'undefined' && document.cookie.includes('fieldcert_csrf=');
      try {
        // Ensure CSRF cookie exists (non-httpOnly) for subsequent mutating requests.
        if (!hasCsrfCookie) {
          await api.get('/auth/csrf');
        }
      } catch {
        // Ignore; backend may already have one or might be unreachable at the moment.
      }

      try {
        const { data } = await api.get('/auth/me');
        if (cancelled) return;
        setUser(data);
      } catch {
        if (cancelled) return;
        setAuthStatus('anonymous');
      }
    }

    // Only run once on mount.
    bootstrap();
    return () => { cancelled = true; };
  }, [setUser, setAuthStatus]);

  if (!_hasHydrated) {
    return (
      <div className="page-loading" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Loading…
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Toaster position="top-right" />
          <Routes>
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/login" element={authStatus === 'authenticated' ? <Navigate to="/" /> : <Login />} />
          <Route path="/forgot-password" element={authStatus === 'authenticated' ? <Navigate to="/" /> : <ForgotPassword />} />
          <Route path="/reset-password" element={authStatus === 'authenticated' ? <Navigate to="/" /> : <ResetPassword />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/force-password-change" element={<ForcePasswordChange />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/sites" element={<Sites />} />
              <Route path="/sites/:id" element={<SiteDetails />} />
              <Route path="/tests" element={<Tests />} />
              <Route path="/repairs" element={<Repairs />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/dev-notes" element={<DevNotes />} />
              <Route path="/dev/components" element={<KitGalleryPage />} />
              <Route path="/super-admin/organizations" element={<SuperAdminOrganizations />} />
              <Route path="/super-admin/admins" element={<PlatformAdmins />} />

              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<SettingsIndexRedirect />} />
                <Route path="organization" element={<Settings />} />
                <Route path="team" element={<TeamSettings />} />
                <Route path="profile" element={<ProfileSettings />} />
                <Route path="appearance" element={<AppearanceSettings />} />
                <Route path="security" element={<SecuritySettings />} />
                <Route path="my-settings" element={<Navigate to="/settings/profile" replace />} />
                <Route path="assets" element={<Assets />} />
                <Route path="sites" element={<Sites />} />
                <Route path="import" element={<Navigate to="/settings/sites" replace />} />
              </Route>

              <Route path="/assets" element={<Navigate to="/settings/assets" replace />} />
              <Route path="/team" element={<Navigate to="/settings/team" replace />} />
              <Route path="/my-settings" element={<Navigate to="/settings/profile" replace />} />
            </Route>
          </Route>
          </Routes>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
